// @flow
import React, { useCallback, useState, useMemo } from 'react';
import { FormatOptionLabelMeta, type OptionType } from '@atlaskit/select';
import styled from 'styled-components';
import Tooltip from '@atlaskit/tooltip';
import { withApollo, WithApolloClient } from 'react-apollo';
import { uniq } from 'lodash';
import CustomAsyncSelect, { CustomAsyncSelectProps } from './CustomAsyncSelect';
import { VIRTUAL_COMPONENTS_AUTOCOMPLETE_QUERY } from '../../graphql/queries';

const MenuOptionWrapper = styled.div`
    display: flex;
    flex-direction: column;
    margin-bottom: 7px;
`;

export interface VirtualComponentOptionType extends OptionType {
    projectNames?: string[];
}

export const parseVirtualComponentToOption = (component: any) => ({
    label: component.name,
    value: component.id,
    projectNames: component.projects?.map(({ jiraProjectName }) => jiraProjectName),
    projects: component.projects,
});

export const parseVirtualComponentsToOptions = (components) => {
    return (components ?? []).map(parseVirtualComponentToOption).sort((a, b) => {
        return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
    });
};

const maxProjectsInDescription = 3;

export const formatVirtualComponentOptionLabel = (
    option: VirtualComponentOptionType,
    { context }: FormatOptionLabelMeta,
) => {
    if (context === 'menu') {
        const countProjectsLeftOut = (option.projectNames?.length ?? 0) - maxProjectsInDescription;
        const projectNames =
            (option.projectNames ?? []).slice(0, maxProjectsInDescription).join(', ') +
            (countProjectsLeftOut > 0 ? `, +${countProjectsLeftOut} more` : '');
        const fullProjectNames = countProjectsLeftOut > 0 ? (option.projectNames ?? []).join(', ') : undefined;

        return (
            <MenuOptionWrapper>
                <div>
                    <strong>{option.label}</strong>
                </div>
                <Tooltip content={fullProjectNames}>
                    {projectNames ? <div style={{ fontSize: 12 }}>{projectNames}</div> : null}
                </Tooltip>
            </MenuOptionWrapper>
        );
    }

    return option.label;
};

export const loadVirtualComponentsAndParseToOptions = async (
    client: ApolloClient,
    searchText: string,
    projectKey?: string,
    excludeComponentIds?: string[],
) => {
    const queryResult = await client.query({
        query: VIRTUAL_COMPONENTS_AUTOCOMPLETE_QUERY,
        variables: {
            searchText,
            projectKey,
            excludeComponentIds,
        },
        fetchPolicy: 'network-only',
    });

    return parseVirtualComponentsToOptions(queryResult.data.virtualComponentsAutocomplete);
};

export interface VirtualComponentSelectProps extends CustomAsyncSelectProps {
    projectKey?: string;
    excludeComponentIds?: string[];
}

const VirtualComponentSelect = ({
    client,
    projectKey,
    excludeComponentIds,
    onChange,
    ...props
}: WithApolloClient<VirtualComponentSelectProps>) => {
    const [selectedOptionsIds, setSelectedOptionsIds] = useState(
        Array.isArray(props.value) ? props.value.map(({ value }) => value) : [],
    );

    const excludeComponentIdsAndSelectedOptionsIds = useMemo(
        () => uniq([...selectedOptionsIds, ...(excludeComponentIds ?? [])]),
        [excludeComponentIds, selectedOptionsIds],
    );

    const onChangeHandler = useCallback(
        (newValue: any, actionMeta: ActionMeta<any>) => {
            setSelectedOptionsIds(Array.isArray(newValue) ? newValue.map(({ value }) => value) : []);
            return onChange?.(newValue, actionMeta);
        },
        [onChange],
    );

    const loadOptions = useCallback(
        async (searchText: string) => {
            return loadVirtualComponentsAndParseToOptions(
                client,
                searchText,
                projectKey,
                excludeComponentIdsAndSelectedOptionsIds,
            );
        },
        [client, excludeComponentIdsAndSelectedOptionsIds, projectKey],
    );

    return (
        <CustomAsyncSelect
            loadOptions={loadOptions}
            cacheOptions
            formatOptionLabel={formatVirtualComponentOptionLabel}
            onChange={onChangeHandler}
            {...props}
        />
    );
};

export default withApollo(VirtualComponentSelect);
